import {
    DROPDOWN_STRING,
    MULTILINK,
    NULL_BOOLEAN,
    OPTIONAL_STRING,
    OPTIONAL_TEXT,
    OPTIONAL_TEXT_3000,
    OPTIONAL_YEAR,
    REQUIRED_STRING,
    YEAR,
} from '@/components/Form/schema';
import { uniqueNameShortField } from '@/forms/forms';
import { GetEventByNameDocument, GetEventSeriesByNameDocument } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { z } from 'zod';

export const CATEGORY_ITEMS = [
    { id: '1', name: 'Messen und Ausstellungen' },
    { id: '2', name: 'Tagungen und Kongresse' },
    { id: '3', name: 'Corporate Events' },
    { id: '4', name: 'Gesellschaftliche Veranstaltungen' },
    { id: '5', name: 'Sonderveranstaltungen' },
    { id: '6', name: 'CV Veranstaltungen' },
    { id: '7', name: 'Koelnmesse konzerninterne Veranstaltungen' },
];

export const TYPE_ITEMS = [
    { id: '1', name: 'Eigenveranstaltung' },
    { id: '2', name: 'Gastmesse' },
    { id: '3', name: 'Ausstellung' },
    { id: '4', name: 'Hausmesse (Closed Shop)' },
    { id: '5', name: 'Kongressmesse' },
    { id: '6', name: 'Märkte und Börsen' },
    { id: '7', name: 'Kongress' },
    { id: '8', name: 'Haupt-, Mitglieder-, Gesellschafterversammlung' },
    { id: '9', name: 'Parteitage und politische Zusammenkünfte' },
    { id: '10', name: 'Tagung, Symposium, Seminar, Konferenz' },
    { id: '11', name: 'Gremiensitzungen' },
    { id: '12', name: 'Prüfungen, Klausuren' },
    { id: '13', name: 'Pressekonferenz' },
    { id: '14', name: 'Showproduktion' },
    { id: '15', name: 'Produktpräsentation & Road Show' },
    { id: '16', name: 'Sportveranstaltungen' },
    { id: '17', name: 'Abiturfeier' },
    { id: '18', name: 'Karnevalsveranstaltung' },
    { id: '19', name: 'Award/Preisverleihung' },
    { id: '20', name: 'Comedyveranstaltung' },
    { id: '21', name: 'Weihnachtsfeier' },
    { id: '22', name: 'Hochzeit' },
    { id: '23', name: 'Bankettveranstaltung' },
    { id: '24', name: 'Geburtstag/Trauerfeier/Jubiläum' },
    { id: '25', name: 'Konzerte' },
    { id: '26', name: 'Film- und Fotoaufnahmen' },
    { id: '27', name: 'Parkplatzvermietung' },
    { id: '28', name: 'Briefwahlzentrum' },
    { id: '29', name: 'Zwischenlager' },
    { id: '30', name: 'Evakuierungen' },
    { id: '31', name: 'Impfzentrum' },
    { id: '32', name: 'Jurysitzung' },
    { id: '33', name: 'Verbandstreffen/Tagungen' },
    { id: '43', name: 'Km-Rahmenveranstaltung' },
    { id: '35', name: 'Kc Rahmenveranstaltung' },
    { id: '36', name: 'Fachbeiratssitzung' },
    { id: '37', name: '(Fach-)Pressekonferenzen' },
    { id: '38', name: 'Gremiensitzungen' },
    { id: '39', name: 'Pressekonferenzen' },
    { id: '40', name: 'Betriebsversammlung' },
    { id: '41', name: 'Belegschaftsversammlung' },
    { id: '42', name: 'Mitarbeiterveranstaltung' },
];

type Mapping = {
    [key: string]: string[];
};

export const CAT_TYPE_MAPPING: Mapping = {
    '1': ['1', '2', '3', '4', '5', '6'],
    '2': ['7', '8', '9', '10', '11', '12'],
    '3': ['13', '14', '15', '16'],
    '4': ['17', '18', '19', '20', '21', '22', '23', '24', '25'],
    '5': ['26', '27', '28', '29', '30', '31'],
    '6': ['32', '33', '34', '35', '36', '37'],
    '7': ['38', '39', '40', '41', '42'],
};

export const CYCLE_ITEMS = [
    { id: '1', name: '1/2 (halbjährlich)' },
    { id: '2', name: '1 (jährlich)' },
    { id: '3', name: '2G (zweijährlich gerade Jahre)' },
    { id: '4', name: '2U (zweijährlich ungerade Jahre)' },
    { id: '5', name: '3 (dreijährlich)' },
    { id: '6', name: '4G (vierjährlich gerade Jahre)' },
    { id: '7', name: '4U (vierjährlich ungerade Jahre)' },
    { id: '8', name: 'unregelmäßig (Wanderveranstaltung)' },
];

const EVENT_SERIES_NAME_SHORT_FIELD = uniqueNameShortField(GetEventSeriesByNameDocument);
const EVENT_NAME_SHORT_FIELD = uniqueNameShortField(GetEventByNameDocument);

export function getEventSeriesUpdateSchema(id: string) {
    return z.object({
        singleEvent: z.boolean(),
        nameShort: uniqueNameShortField(GetEventSeriesByNameDocument, id),
        nameLong: REQUIRED_STRING,
        subtitle: OPTIONAL_TEXT,
        status: REQUIRED_STRING,
        numberShort: OPTIONAL_STRING,
        foundationYear: OPTIONAL_YEAR, // z.number().int().max(2099).optional()
        notes: OPTIONAL_TEXT_3000,
        description: OPTIONAL_TEXT_3000,
        category: OPTIONAL_STRING, // z.enum(CATEGORY_CHOICES)
        type: OPTIONAL_STRING, // z.enum(TYPE_CHOICES)
        isTechnical: NULL_BOOLEAN,
        isPublic: NULL_BOOLEAN,
        partnersOverviewName: OPTIONAL_STRING,
        partnersOverviewLink: OPTIONAL_STRING,
        additionalPartners: OPTIONAL_STRING,
        companyGroup: OPTIONAL_STRING,
        businessDomain: OPTIONAL_STRING,
        department: OPTIONAL_STRING,
        organisationalChanges: OPTIONAL_STRING,
        notesDayOrder: OPTIONAL_STRING,
        notesSetup: OPTIONAL_STRING,
        notesEarlyConstructionInternal: OPTIONAL_STRING,
        notesEarlyConstructionExternal: OPTIONAL_STRING,
        notesConstruction: OPTIONAL_STRING,
        notesExecution: OPTIONAL_STRING,
        notesDismantling: OPTIONAL_STRING,
        notesExtendedDismantlingExternal: OPTIONAL_STRING,
        notesExtendedDismantlingInternal: OPTIONAL_STRING,
        notesTechnicalDismantling: OPTIONAL_STRING,
        notesDayOrderHistory: OPTIONAL_STRING,
        notesAppointmentRules: OPTIONAL_STRING,
        notesMonths: OPTIONAL_STRING,
        notesHolidaysNrw: OPTIONAL_STRING,
        notesHolidaysNational: OPTIONAL_STRING,
        notesHolidaysInternational: OPTIONAL_STRING,
        notesPublicHolidaysNrw: OPTIONAL_STRING,
        notesPublicHolidaysNational: OPTIONAL_STRING,
        notesPublicHolidaysInternational: OPTIONAL_STRING,
        otherNotesAppointmentRules: OPTIONAL_STRING,
        appointmentHistory: OPTIONAL_STRING,
        cycle: OPTIONAL_STRING, // z.enum(CYCLE_CHOICES)
        cycleChanges: OPTIONAL_STRING,
        allocationRules: OPTIONAL_STRING,
        locationHistory: OPTIONAL_STRING,
        notesHallExhibitionAreas: OPTIONAL_STRING,
        notesBoulevardSections: OPTIONAL_STRING,
        notesPassages: OPTIONAL_STRING,
        notesEntrances: OPTIONAL_STRING,
        notesServiceCenters: OPTIONAL_STRING,
        notesConferenceRooms: OPTIONAL_STRING,
        notesOfficesInternal: OPTIONAL_STRING,
        notesStorageInternal: OPTIONAL_STRING,
        notesOfficesExternal: OPTIONAL_STRING,
        notesStorageExternal: OPTIONAL_STRING,
        notesAreas: OPTIONAL_STRING,
        notesParking: OPTIONAL_STRING,
        otherAllocationNeeds: OPTIONAL_STRING,
        iconUpload: OPTIONAL_STRING,
    });
}

export const EventSeriesCreateSchema = z.object({
    singleEvent: z.boolean(),
    nameShort: EVENT_SERIES_NAME_SHORT_FIELD,
    nameLong: REQUIRED_STRING,
    subtitle: OPTIONAL_TEXT,
});

export type EventSeriesCreateSchemaType = z.infer<typeof EventSeriesCreateSchema>;

export function getEventSeriesIdentifiersSchema(id: string) {
    return z.object({
        nameShort: uniqueNameShortField(GetEventSeriesByNameDocument, id),
        nameLong: REQUIRED_STRING,
        subtitle: OPTIONAL_TEXT,
        websites: MULTILINK,
        numberKapman: z.string(),
        numberShort: z.string(),
        foundationYear: OPTIONAL_YEAR,
        notes: OPTIONAL_TEXT_3000,
        description: OPTIONAL_TEXT_3000,
        iconUpload: OPTIONAL_STRING,
    });
}

export const EventSeriesTypesSchema = z.object({
    category: OPTIONAL_STRING,
    type: OPTIONAL_STRING,
    isTechnical: NULL_BOOLEAN,
    isPublic: NULL_BOOLEAN,
});

export const EventSeriesPartnersSchema = z.object({
    partnersOverviewName: z.string(),
    partnersOverviewLink: z.string(),
    organizers: MULTILINK,
    executors: MULTILINK,
    sponsors: MULTILINK,
    additionalPartners: OPTIONAL_TEXT,
});

export const EventSeriesParallelsSchema = z.object({
    favoredParallelsInternal: MULTILINK,
    favoredParallelsExternal: MULTILINK,
});

export const EventSeriesDocumentsSchema = z.object({
    extraDocuments: MULTILINK,
});

export const EventSeriesSystemsSchema = z.object({
    extraSystems: MULTILINK,
});

export const EventSeriesDataSchema = z.object({
    externalData: MULTILINK,
    internalData: MULTILINK,
});

export const EventSeriesSubEventsSchema = z.object({
    subeventsLocal: MULTILINK,
    subeventsOtherLocations: MULTILINK,
});

export const EventSeriesFairAbroadSchema = z.object({
    fairAbroad: MULTILINK,
    fairAbroadCooperation: MULTILINK,
});

export const EventSeriesCompetitionSchema = z.object({
    competitionNational: MULTILINK,
    otherCompetitionNational: MULTILINK,
    competitionInternational: MULTILINK,
    otherCompetitionInternational: MULTILINK,
});

export const EventSeriesOtherLinksSchema = z.object({
    otherLinksInternal: MULTILINK,
    otherLinksExternal: MULTILINK,
});

export const EventSeriesOrganisation = z.object({
    companyGroup: z.string(),
    businessDomain: z.string(),
    department: z.string(),
    organisationalChanges: OPTIONAL_TEXT,
});

export const EventSeriesDayOrder = z.object({
    notesDayOrder: OPTIONAL_TEXT,
    notesSetup: OPTIONAL_TEXT,
    notesEarlyConstructionInternal: OPTIONAL_TEXT,
    notesEarlyConstructionExternal: OPTIONAL_TEXT,
    notesConstruction: OPTIONAL_TEXT,
    notesExecution: OPTIONAL_TEXT,
    notesDismantling: OPTIONAL_TEXT,
    notesExtendedDismantlingExternal: OPTIONAL_TEXT,
    notesExtendedDismantlingInternal: OPTIONAL_TEXT,
    notesTechnicalDismantling: OPTIONAL_TEXT,
    notesDayOrderHistory: OPTIONAL_TEXT,
});

export const EventSeriesNotesAppointmentRules = z.object({
    notesAppointmentRules: OPTIONAL_TEXT,
    notesMonths: OPTIONAL_TEXT,
    notesHolidaysNrw: OPTIONAL_TEXT,
    notesHolidaysNational: OPTIONAL_TEXT,
    notesHolidaysInternational: OPTIONAL_TEXT,
    notesPublicHolidaysNrw: OPTIONAL_TEXT,
    notesPublicHolidaysNational: OPTIONAL_TEXT,
    notesPublicHolidaysInternational: OPTIONAL_TEXT,
    otherNotesAppointmentRules: OPTIONAL_TEXT,
    appointmentHistory: OPTIONAL_TEXT,
});

export const EventSeriesCycle = z.object({
    cycle: OPTIONAL_STRING,
    cycleChanges: OPTIONAL_TEXT,
});

export const EventSeriesAllocationRules = z.object({
    allocationRules: OPTIONAL_TEXT,
    locationHistory: OPTIONAL_TEXT,
});

export const EventSeriesAllocationNeeds = z.object({
    notesHallExhibitionAreas: OPTIONAL_TEXT,
    notesBoulevardSections: OPTIONAL_TEXT,
    notesPassages: OPTIONAL_TEXT,
    notesEntrances: OPTIONAL_TEXT,
    notesServiceCenters: OPTIONAL_TEXT,
    notesConferenceRooms: OPTIONAL_TEXT,
    notesOfficesInternal: OPTIONAL_TEXT,
    notesStorageInternal: OPTIONAL_TEXT,
    notesOfficesExternal: OPTIONAL_TEXT,
    notesStorageExternal: OPTIONAL_TEXT,
    notesAreas: OPTIONAL_TEXT,
    notesParking: OPTIONAL_TEXT,
    otherAllocationNeeds: OPTIONAL_TEXT,
});

export function getEventUpdateSchema(id: string) {
    return z.object({
        nameShort: uniqueNameShortField(GetEventByNameDocument, id),
        nameLong: OPTIONAL_STRING,
        subtitle: OPTIONAL_STRING,
        executionYear: YEAR,
        description: OPTIONAL_TEXT_3000,
        annualSpecifics: OPTIONAL_STRING,
        companyGroup: OPTIONAL_STRING,
        businessDomain: OPTIONAL_STRING,
        department: OPTIONAL_STRING,
        responsible: OPTIONAL_STRING,
        assistant: OPTIONAL_STRING,
        teamChanges: OPTIONAL_STRING,
        status: REQUIRED_STRING,
        iconUpload: OPTIONAL_STRING,
    });
}

export const EventCreateSchema = z.object({
    nameShort: EVENT_NAME_SHORT_FIELD,
    nameLong: z.string().min(1, $t('input-required')).trim(),
    executionYear: YEAR,
    status: DROPDOWN_STRING,
});

export type EventCreateSchemaType = z.infer<typeof EventCreateSchema>;

export function getEventBaseDataSchema(id: string) {
    return z.object({
        nameShort: uniqueNameShortField(GetEventByNameDocument, id),
        nameLong: z.string(),
        subtitle: z.string(),
        status: z.string(),
        executionYear: YEAR,
        description: z.string(),
        annualSpecifics: z.string(),
        iconUpload: z.string(),
    });
}

export const EventTeamStructureSchema = z.object({
    responsible: z.string(),
    assistant: z.string(),
    teamChanges: OPTIONAL_TEXT,
});

export const EventOtherLinksSchema = z.object({
    otherLinksInternal: MULTILINK,
    otherLinksExternal: MULTILINK,
});

export const EventDocumentsSchema = z.object({
    extraDocuments: MULTILINK,
});

export const EventFairAbroadSchema = z.object({
    fairAbroad: MULTILINK,
    fairAbroadCooperation: MULTILINK,
});

export const EventOrganisationSchema = z.object({
    companyGroup: z.string(),
    businessDomain: z.string(),
    department: z.string(),
});

export const EventParallelsSchema = z.object({
    favoredParallelsInternal: MULTILINK,
    favoredParallelsExternal: MULTILINK,
});

export const EventSubEventsSchema = z.object({
    subeventsLocal: MULTILINK,
    subeventsOtherLocations: MULTILINK,
});

export const EventSystemsSchema = z.object({
    extraSystems: MULTILINK,
});

export const EventDataSchema = z.object({
    externalData: MULTILINK,
    internalData: MULTILINK,
});

export const UpdateEventVariantNameSchema = z.object({
    name: OPTIONAL_STRING,
});
